import Matchmaking, { STATES } from "../model/matchmaking";
import api from "./api";
import routes from "./apiRoutes.json";

const channel = "matchmaking";
const serverUrl = process.env.REACT_APP_GO;

enum TYPES {
    LOOK = "LOOK",
    REQUEST = "REQUEST",
    CONFIRM = "CONFIRM",
    REJECT = "REJECT",
    READY = "READY",
}

interface message {
    type: TYPES,
    origId: string,
}

interface redisMessage {
    id: string,
    content: string,
}

class RedisMatchmaking {
    matchMakingSocket: WebSocket;
    clientSocket: WebSocket;
    matchMakingState: Matchmaking;
    setMatchMakingState: Function;
    origId: string;
    requestId: string = "";
    repeatLook: NodeJS.Timeout | undefined;
    escapeTimeout: NodeJS.Timeout | undefined;

    opponentIsReady: boolean;
    setOpponent: Function;
    setChannel: Function;
    categoryIds: string[];

    constructor(matchMakingState: Matchmaking, setMatchMakingState: Function,
        setOpponentId: Function, setChannelId: Function, origId: string, categoryIds: string[]) {
        this.matchMakingState = matchMakingState;
        this.setMatchMakingState = setMatchMakingState;
        this.origId = origId;
        this.opponentIsReady = false;
        this.categoryIds = categoryIds;

        this.setOpponent = setOpponentId;
        this.setChannel = setChannelId;

        this.matchMakingSocket = new WebSocket(`${serverUrl}${channel}${this.categoryIds.join("")}`);
        this.clientSocket = new WebSocket(`${serverUrl}${origId}`);

        this.matchMakingSocket.onmessage = (event) => {
            this.handleMatchMakingMessage(event.data);
        };

        this.clientSocket.onmessage = (event) => {
            this.handleClientMessage(event.data);
        };
    }

    createMessage = (type: TYPES): message => ({
        type: type,
        origId: this.origId || "-1",
    })

    publish = (socket: WebSocket, channel: string, type: TYPES): void => {
        const redisMessage: redisMessage = {
            id: channel,
            content: JSON.stringify(this.createMessage(type)),
        };
        socket.send(JSON.stringify(redisMessage));
    }

    sendLookForGame = async (): Promise<void> => {
        this.requestId = "";
        this.opponentIsReady = false;
        this.setMatchMakingState(STATES.LOOK);
        while (!this.matchMakingSocket.readyState) {
            await new Promise((resolve) => setTimeout(resolve, 50));
        }
        this.publish(this.matchMakingSocket, `${channel}${this.categoryIds.join("")}`, TYPES.LOOK);
        this.setLookInterval();
    }

    setLookInterval = () => {
        this.repeatLook = setInterval(() => {
            this.publish(this.matchMakingSocket, `${channel}${this.categoryIds.join("")}`, TYPES.LOOK);
        }, 5000);
    }
    unsetLookInterval = () => {
        if (this.repeatLook) {
            clearInterval(this.repeatLook);
            this.repeatLook = undefined;
        }
    }

    setEscapeTimeout = () => {
        this.escapeTimeout = setTimeout(() => {
            this.publish(this.clientSocket, this.requestId, TYPES.REJECT);
            this.requestId = "";
            this.unsetEscapeTimeout();
            this.setMatchMakingState(STATES.LOOK);
        }, 1000);

    }

    unsetEscapeTimeout = () => {
        if (this.escapeTimeout) {
            clearTimeout(this.escapeTimeout);
            this.escapeTimeout = undefined;
        }
    }

    parseData = (data: string): message => {
        const json: redisMessage = JSON.parse(data);
        const content: message = JSON.parse(json.content);
        return content;
    }

    handleMatchMakingMessage = (data: string): void => {
        const content: message = this.parseData(data);
        if (content.origId === this.origId)
            return; // own message
        if (this.matchMakingState.state !== STATES.LOOK) // only accept matchmaking messages when looking for one
            return;
        if (content.type === TYPES.LOOK) {
            // player is valid for a request
            this.unsetLookInterval();
            this.setEscapeTimeout();
            this.requestId = content.origId;
            this.publish(this.matchMakingSocket, this.requestId, TYPES.REQUEST);
            this.setMatchMakingState(STATES.WAIT_FOR_REQUEST); // wait for confirmation
        }
    }

    confirmGame = (origId: string) => {
        this.setOpponent(origId);
        this.setMatchMakingState(STATES.CONFIRM);
    }

    rejectAndLook = () => {
        if (this.matchMakingState.opponentId !== "") {
            this.publish(this.clientSocket, this.matchMakingState.opponentId, TYPES.REJECT);
            this.setOpponent("");
            this.opponentIsReady = false;
            this.requestId = "";
        }
        this.setMatchMakingState(STATES.LOOK); // maybe change to sendLookForGame
    }

    sendIsReady = () => {
        if (this.matchMakingState.opponentId !== "") {
            this.publish(this.clientSocket, this.matchMakingState.opponentId, TYPES.READY);
            if (this.opponentIsReady && this.matchMakingState.state === STATES.CONFIRM) {
                this.sendStartGame();
            } else
                this.setMatchMakingState(STATES.WAITING_FOR_OPPONENT);
        }
    }

    sendStartGame = () => {
        this.setMatchMakingState(STATES.SEND_START_GAME);
        api.post(
            routes.startGame,
            {
                player2: this.matchMakingState.opponentId,
                categories: this.categoryIds,
            },
        ).then((response) => {
            const channelId = response.data.data;
            this.setChannel(channelId);
            this.setMatchMakingState(STATES.START_GAME);
        },
        ).catch((err) => {
            // eslint-disable-next-line no-console
            console.log(err);
            this.rejectAndLook();
        });
    }

    handleClientMessage = (data: string): void => {
        const content: message = this.parseData(data);
        switch (content.type) {
            // a request for a game was received
            case TYPES.REQUEST:
                if (this.matchMakingState.state === STATES.LOOK) { // if I am looking for a game
                    // if confirm, then do the following
                    this.unsetLookInterval();
                    this.publish(this.clientSocket, content.origId, TYPES.CONFIRM);
                    this.confirmGame(content.origId);
                } else { // otherwise reject the request
                    if (content.origId !== this.matchMakingState.opponentId && content.origId !== this.requestId) {
                        this.publish(this.clientSocket, content.origId, TYPES.REJECT);
                    }
                }
                break;
            case TYPES.CONFIRM:
                if (this.matchMakingState.state === STATES.WAIT_FOR_REQUEST) {
                    if (this.requestId !== content.origId)
                        return;
                    this.unsetEscapeTimeout();
                    this.requestId = "";
                    this.confirmGame(content.origId);
                }
                break;
            case TYPES.READY:
                if (content.origId !== this.matchMakingState.opponentId)
                    return;
                if (this.matchMakingState.state !== STATES.CONFIRM && this.matchMakingState.state !== STATES.WAITING_FOR_OPPONENT)
                    return;
                this.requestId = "";
                this.opponentIsReady = true;
                if (this.matchMakingState.state === STATES.WAITING_FOR_OPPONENT) {
                    this.sendStartGame();
                }
                break;
            case TYPES.REJECT:
                if (content.origId !== this.requestId && content.origId !== this.matchMakingState.opponentId)
                    return;
                this.requestId = "";
                this.matchMakingState.opponentId = "";
                this.opponentIsReady = false;
                this.setOpponent("");
                this.setMatchMakingState(STATES.LOOK);
                this.setLookInterval();
                this.unsetEscapeTimeout();
                break;
            default:
                break;
        }
        return;
    }

    close = () => {
        this.unsetLookInterval();
        if (this.requestId !== "") {
            this.publish(this.clientSocket, this.requestId, TYPES.REJECT);
            this.requestId = "";
        }
        if (this.matchMakingState.opponentId !== "") {
            if (this.matchMakingState.state !== STATES.SEND_START_GAME && this.matchMakingState.state !== STATES.START_GAME)
                this.publish(this.clientSocket, this.matchMakingState.opponentId, TYPES.REJECT);
        }
        setTimeout(() => {
            this.matchMakingSocket.close();
            this.clientSocket.close();
        }, 500);
    }

}

let instance: RedisMatchmaking | undefined = undefined;

const setRedisMatchMaking = (matchMakingState: Matchmaking): void => {
    if (instance === undefined)
        return;
    instance.matchMakingState = matchMakingState;

};

const createRedisMatchMaking = (matchMakingState: Matchmaking, setMatchMakingState: Function,
    setOpponentId: Function, setChannelId: Function, userId: string, categoryIds: string[]): RedisMatchmaking => {
    if (instance)
        instance.close();
    instance = new RedisMatchmaking(matchMakingState, setMatchMakingState, setOpponentId, setChannelId, userId, categoryIds);
    return instance;
};

const getRedisMatchMakingSimple = (): RedisMatchmaking | undefined => instance;

export { getRedisMatchMakingSimple, setRedisMatchMaking };


export default createRedisMatchMaking;
